import { makePriceTraceStep } from "./priceTraceStepFactory";
import { detectPriceTraceAnomalies } from "./priceTraceAnomalyDetector";
import { formatPercent, formatTraceNumber } from "./priceTraceFormatters";
import { resolveLdsSupplierCodeForPricing } from "../../../utils/resolveLdsSupplierCodeForPricing";
import { resolveOrganizedTourSupplierCommissionDisplay } from "./resolveOrganizedTourSupplierCommissionDisplay";
import { resolveOrganizedTourPrimaryPriceTrace } from "./resolveOrganizedTourPrimaryPriceTrace";
import { resolveOrganizedTourDisplayOrderNumber } from "../../../utils/resolveOrganizedTourDisplayOrderNumber.js";

const NA = "לא זמין";

function num(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function strOrNa(v) {
  if (v == null || v === "") return NA;
  const s = String(v).trim();
  return s || NA;
}

function fmtIls(n) {
  if (n == null || !Number.isFinite(Number(n))) return NA;
  return `${Math.round(Number(n)).toLocaleString("he-IL")} ₪`;
}

function pickOurPrice(deal) {
  const root = deal && typeof deal === "object" ? deal : {};
  return (
    root.ourPrice ||
    root.fullDeal?.ourPrice ||
    (root.price && typeof root.price === "object" ? root.price.ourPrice : null) ||
    null
  );
}

function pickEntityName(deal) {
  const d = deal && typeof deal === "object" ? deal : {};
  return (
    d.tourName ||
    d.name ||
    d.title ||
    d.hotel?.hotelName ||
    d.hotel?.name ||
    NA
  );
}

function supplierInOrigCurrency(otf, snap, dealFare) {
  const fromSnap = num(snap?.supplier_price_original) ?? num(snap?.supplier_gross_source);
  if (fromSnap != null) {
    return {
      val: fromSnap,
      source: snap?.supplier_price_original != null
        ? "pricingSnapshot.supplier_price_original"
        : "pricingSnapshot.supplier_gross_source",
    };
  }
  const fromOtf = num(otf.supplierGrossSource) ?? num(otf.ldsPrice);
  if (fromOtf != null) {
    return {
      val: fromOtf,
      source: otf.supplierGrossSource != null ? "ourTotalFare.supplierGrossSource" : "ourTotalFare.ldsPrice",
    };
  }
  const api = num(dealFare?.totalFare);
  if (api != null) return { val: api, source: "dealFare.totalFare" };
  return { val: null, source: "" };
}

function resolveDisplayedIls(deal) {
  const our = pickOurPrice(deal);
  const otf = our?.ourTotalFare && typeof our.ourTotalFare === "object" ? our.ourTotalFare : {};
  const direct =
    num(otf.priceWithCommissionAndCreditCardFee) ?? num(our?.priceWithCreditCardFee) ?? null;
  if (direct != null) return direct;
  return null;
}

const SNAPSHOT_KEYS_TRACE =
  "supplier_rule,supplier_commission_type,supplier_price_original,supplier_deduction,supplier_net_price,hr_markup,hr_markup_line_item_ils,hr_margin_embedded_ils,hr_margin_pricing_mode,agent_markup,credit_card_fee,final_price,currency,exchange_rates_used,hr_margin_resolver_trace,lds_supplier_code_resolved,pricing_engine_version";

function stringifyTraceSlice(obj, maxLen = 900) {
  if (obj == null) return NA;
  try {
    let j = typeof obj === "string" ? obj : JSON.stringify(obj, null, 0);
    if (typeof j === "string" && j.length > maxLen) j = `${j.slice(0, maxLen)}…`;
    return j || NA;
  } catch {
    return NA;
  }
}

/**
 * Admin price trace for LDS Organized Tour (pricingSnapshot + ourTotalFare SSOT).
 * Does not recalculate prices — displays whatever exists on the deal payload.
 *
 * @param {{
 *   deal: object|null|undefined,
 *   context?: string,
 *   ldsOrderRow?: object|null,
 *   displayIls?: number|null,
 *   calculateLDSDealFareForRoomsComposition?: Function|null,
 * }} params
 */
export function buildOrganizedTourPriceTrace({
  deal,
  context = "unknown",
  ldsOrderRow = null,
  displayIls = null,
  calculateLDSDealFareForRoomsComposition = null,
}) {
  const root = deal && typeof deal === "object" ? deal : {};
  const ourPrice = pickOurPrice(root);
  const otf = ourPrice?.ourTotalFare && typeof ourPrice.ourTotalFare === "object" ? ourPrice.ourTotalFare : {};
  const snap = ourPrice?.pricingSnapshot && typeof ourPrice.pricingSnapshot === "object" ? ourPrice.pricingSnapshot : null;
  const hrTrace = snap?.hr_margin_resolver_trace && typeof snap.hr_margin_resolver_trace === "object"
    ? snap.hr_margin_resolver_trace
    : null;
  const dealFare = root.dealFare || root.fullDeal?.dealFare || {};
  const fareDetails = root.fareDetails || root.fullDeal?.fareDetails || {};
  const supplierDetails = root.supplierDetails || root.fullDeal?.supplierDetails || {};

  const entityId = root.dealId ?? root.ldsDealId ?? NA;
  const entityName = pickEntityName(root);

  let displayOrderNumberForAgent = null;
  if (ldsOrderRow && typeof ldsOrderRow === "object") {
    const row = /** @type {Record<string, unknown>} */ (ldsOrderRow);
    const rowOur = row.ourPrice ?? row.ourprice;
    const r = resolveOrganizedTourDisplayOrderNumber({
      order: row,
      technicalOrderId: row.id ?? row.ourLdsId ?? row.orderId ?? row.orderid,
      ourPrice:
        ourPrice && typeof ourPrice === "object"
          ? ourPrice
          : rowOur && typeof rowOur === "object"
            ? rowOur
            : null,
      selectedResult: root,
    });
    displayOrderNumberForAgent = r.displayNumber;
  }

  const srcCurrencyRaw =
    otf.fx_currency || otf.originalCurrency || fareDetails.currency || snap?.currency || null;
  const srcCurrency = strOrNa(srcCurrencyRaw);

  const idx = { n: 1 };
  const steps = [];
  const push = (partial) => steps.push(makePriceTraceStep(idx, partial));

  const primary = resolveOrganizedTourPrimaryPriceTrace({
    deal: root,
    displayIls,
    calculateLDSDealFareForRoomsComposition,
  });

  const supOrig = supplierInOrigCurrency(otf, snap, dealFare);
  const fxRate = primary.fxRate;
  const fxSource = primary.fxRateSource;
  const supplierNet =
    num(snap?.supplier_net_price) ??
    num(snap?.supplier_net) ??
    num(otf.supplierNetSource) ??
    num(otf.netPrice);

  const snapForCommission =
    snap || primary.rule
      ? { ...(snap && typeof snap === "object" ? snap : {}), supplier_rule: snap?.supplier_rule || primary.rule }
      : snap;

  const supplierCommission = resolveOrganizedTourSupplierCommissionDisplay({
    snap: snapForCommission,
    fareDetails,
    gross: supOrig.val,
    net: supplierNet,
  });

  let supplierIls = null;
  if (supplierNet != null && fxRate != null && fxRate > 0) {
    supplierIls = Math.ceil(supplierNet * fxRate);
  } else if (num(snap?.supplier_net_price) != null && fxRate != null) {
    supplierIls = Math.ceil(num(snap.supplier_net_price) * fxRate);
  }

  const hrPct =
    num(hrTrace?.final_hr_margin_percent) ??
    num(otf.hrCommissionPercent) ??
    num(otf.unifiedHrMarginPercent);
  const hrAmountIls =
    num(hrTrace?.calculated_hr_margin_ils) ??
    num(otf.hrCommission) ??
    num(snap?.hr_markup_line_item_ils);

  const initialAgent = num(otf.initialPrice);
  const priceAfterHr = num(otf.priceWithCommission);
  const ccFee = num(otf.creditCardFee) ?? num(snap?.credit_card_fee);
  const withCc = num(otf.priceWithCommissionAndCreditCardFee) ?? num(snap?.final_price);
  const handling = num(otf.handlingFee) ?? num(ourPrice?.handlingFee);
  const baggage = num(ourPrice?.baggagePrice);

  const displayed =
    primary.agentNetIls != null ? primary.agentNetIls : resolveDisplayedIls(root);
  const snapFinal = num(snap?.final_price);

  push({
    layer: "identifiers",
    field: "offer_deal_id",
    rawValue: strOrNa(entityId !== NA ? String(entityId) : null),
    normalized: entityId !== NA ? entityId : null,
    currency: "—",
    notes: `הקשר UI: ${context}`,
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "identifiers",
    field: "supplier_id",
    rawValue:
      supplierDetails.supplierId != null
        ? String(supplierDetails.supplierId)
        : supplierDetails.id != null
          ? String(supplierDetails.id)
          : NA,
    normalized: supplierDetails.supplierId ?? supplierDetails.id ?? null,
    currency: "—",
    notes: "מקור: supplierDetails",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  const supplierCodeResolvedSsot = resolveLdsSupplierCodeForPricing(root);

  push({
    layer: "identifiers",
    field: "supplier_code",
    rawValue:
      supplierDetails.supplierCode != null
        ? String(supplierDetails.supplierCode)
        : supplierDetails.code != null
          ? String(supplierDetails.code)
          : NA,
    normalized: supplierCodeResolvedSsot,
    currency: "—",
    notes: "SSOT resolver (parity BE): כולל supplierDetails / ldsCommissionCondition / pricingSnapshot",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  const ovHrAudit =
    hrTrace?.supplier_hr_margin_override_audit && typeof hrTrace.supplier_hr_margin_override_audit === "object"
      ? hrTrace.supplier_hr_margin_override_audit
      : null;

  push({
    layer: "identifiers",
    field: "lds_supplier_code_resolved",
    rawValue:
      snap?.lds_supplier_code_resolved != null ? String(snap.lds_supplier_code_resolved) : NA,
    normalized: snap?.lds_supplier_code_resolved ?? supplierCodeResolvedSsot,
    currency: "—",
    notes: "מ־pricingSnapshot (מנוע תמחור); אם חסר — מוצג גם צעד resolver לעיל",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "hr_margin_ssot",
    field: "normalized_product_type",
    rawValue: strOrNa(
      hrTrace?.product_type ?? hrTrace?.dealtype_product_type ?? root.dealType ?? root.dealtype ?? null,
    ),
    normalized: hrTrace?.product_type ?? hrTrace?.dealtype_product_type ?? null,
    currency: "—",
    commission_type:
      hrTrace?.commission_type != null ? String(hrTrace.commission_type) : NA,
    notes: "מטרת bucket HR: organized_tour / vacation_package / וכו׳ (מתוך hr_margin_resolver_trace כשיש)",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  const ovMatchRaw =
    hrTrace?.supplier_override_match ?? ovHrAudit?.supplier_override_match ?? null;

  push({
    layer: "hr_margin_ssot",
    field: "supplier_override_match",
    rawValue:
      ovMatchRaw === true ? "כן" : ovMatchRaw === false ? "לא" : NA,
    normalized: ovMatchRaw,
    currency: "—",
    commission_value:
      hrTrace?.supplier_hr_margin_override_percent != null
        ? formatPercent(Number(hrTrace.supplier_hr_margin_override_percent))
        : ovHrAudit?.supplier_hr_margin_override_percent != null
          ? formatPercent(Number(ovHrAudit.supplier_hr_margin_override_percent))
          : NA,
    notes:
      (hrTrace?.supplier_hr_margin_override_miss_reason ??
        ovHrAudit?.supplier_hr_margin_override_miss_reason ??
        hrTrace?.fallback_reason ??
        ovHrAudit?.fallback_reason) != null
        ? String(
            hrTrace?.supplier_hr_margin_override_miss_reason ??
              ovHrAudit?.supplier_hr_margin_override_miss_reason ??
              hrTrace?.fallback_reason ??
              ovHrAudit?.fallback_reason,
          )
        : "מתאים ספק + עמודת HR ב־lds_suppliers לפני פיצ'רי חבילה",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  const selectedRuleSlice =
    hrTrace?.selected_supplier_rule ??
    ovHrAudit?.selected_supplier_rule ??
    snap?.supplier_rule ??
    null;

  push({
    layer: "hr_margin_ssot",
    field: "selected_supplier_rule",
    rawValue: stringifyTraceSlice(selectedRuleSlice),
    normalized: selectedRuleSlice && typeof selectedRuleSlice === "object" ? selectedRuleSlice : null,
    currency: "—",
    notes: "כלל מתאים מתוך טבלת lds_admin (sanitize) או supplier_rule בסנאפשוט",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "api",
    field: "supplier_price_source_currency",
    rawValue: supOrig.val != null ? formatTraceNumber(supOrig.val) : NA,
    normalized: supOrig.val,
    currency: srcCurrency !== NA ? srcCurrency : "—",
    exchangeRate: fxRate != null ? formatTraceNumber(fxRate) : NA,
    notes: [
      primary.apiUnit ? `יחידה: ${primary.apiUnit}` : null,
      supOrig.source ? `מקור: ${supOrig.source}` : null,
    ]
      .filter(Boolean)
      .join(" · ") || NA,
    fx_source: fxSource || NA,
    fx_rate: fxRate != null ? String(fxRate) : NA,
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "supplierInOrigCurrency",
    priceChanged: false,
  });

  push({
    layer: "supplier_commission",
    field: "supplier_commission_type",
    rawValue:
      supplierCommission.supplier_commission_type !== "none"
        ? `${supplierCommission.supplier_commission_type} · ${supplierCommission.supplier_commission_type_he}`
        : supplierCommission.supplier_commission_type,
    normalized: supplierCommission.supplier_commission_type,
    currency: "—",
    notes: supplierCommission.type_source
      ? `מקור סיווג: ${supplierCommission.type_source}`
      : "ללא כלל ספק מזוהה — none",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "resolveOrganizedTourSupplierCommissionDisplay",
    priceChanged: false,
  });

  push({
    layer: "supplier_commission",
    field: "supplier_commission_value",
    rawValue:
      supplierCommission.supplier_commission_value_display != null
        ? String(supplierCommission.supplier_commission_value_display)
        : NA,
    normalized: supplierCommission.supplier_commission_value_display,
    currency: supplierCommission.currency !== "—" ? supplierCommission.currency : "—",
    notes: "תצורה מוצהרת (אחוז / פיקס) — בלי הערכות כשאין נתון",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "resolveOrganizedTourSupplierCommissionDisplay",
    priceChanged: false,
  });

  push({
    layer: "supplier_commission",
    field: "supplier_commission_amount_source_currency",
    rawValue: supplierCommission.supplier_commission_amount_source_currency_display,
    normalized: supplierCommission.deduction,
    currency: supplierCommission.currency !== "—" ? supplierCommission.currency : "—",
    notes:
      supplierCommission.deductionStatusMessage != null
        ? `סטטוס: ${supplierCommission.deductionStatusMessage}`
        : "ניכוי במטבע מקור — supplier_deduction או gross−net",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "resolveOrganizedTourSupplierCommissionDisplay",
    priceChanged: false,
  });

  const primaryByKeyEarly = (key) =>
    Array.isArray(primary.primaryRows) ? primary.primaryRows.find((r) => r.key === key) : null;

  push({
    layer: "supplier_commission",
    field: "supplier_commission_per_person",
    rawValue: primaryByKeyEarly("supplier_commission_per_person")?.value ?? NA,
    normalized: primary.supplierCommissionPerPerson,
    currency: primary.srcCurrency !== "—" ? primary.srcCurrency : "—",
    passengers: primary.pax != null ? primary.pax : "—",
    notes: primary.supplierCommissionPerPersonSource || primaryByKeyEarly("supplier_commission_per_person")?.hint || "עמלת ספק לאדם מכלל האדמין / ניכוי",
    sourceFile: "features/admin-price-trace/utils/resolveOrganizedTourPrimaryPriceTrace.js",
    sourceFunction: "resolveOrganizedTourPrimaryPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "supplier_commission",
    field: "supplier_commission_total",
    rawValue: primaryByKeyEarly("supplier_commission_total")?.value ?? NA,
    normalized: primary.supplierCommissionTotal,
    currency: primary.srcCurrency !== "—" ? primary.srcCurrency : "—",
    passengers: primary.pax != null ? primary.pax : "—",
    notes: primary.supplierCommissionTotalSource || primaryByKeyEarly("supplier_commission_total")?.hint || "סה״כ עמלה = לאדם × נוסעים",
    sourceFile: "features/admin-price-trace/utils/resolveOrganizedTourPrimaryPriceTrace.js",
    sourceFunction: "resolveOrganizedTourPrimaryPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "admin_discount",
    field: "admin_discount_lds_suppliers",
    rawValue: primaryByKeyEarly("admin_discount")?.value ?? NA,
    normalized: primary.discountPct,
    currency: primary.srcCurrency !== "—" ? primary.srcCurrency : "—",
    discount: primary.discountPct != null ? formatPercent(primary.discountPct) : NA,
    notes: primary.ruleMatchHint || "הנחה מטבלת אדמין (lds_suppliers.discount_percentage)",
    sourceFile: "features/admin-price-trace/utils/resolveOrganizedTourPrimaryPriceTrace.js",
    sourceFunction: "resolveOrganizedTourPrimaryPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "supplier_commission",
    field: "supplier_commission_percent_effective",
    rawValue:
      supplierCommission.supplier_commission_percent_effective_display != null
        ? supplierCommission.supplier_commission_percent_effective_display
        : NA,
    normalized: supplierCommission.supplier_commission_percent_effective,
    currency: "—",
    notes:
      supplierCommission.supplier_commission_percent_effective_display != null
        ? "deduction ÷ API gross (רק כששניהם ידועים)"
        : "לא מחושב — חסר נתון או ניכוי לא זמין",
    commissionPercent: supplierCommission.supplier_commission_percent_effective_display,
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "resolveOrganizedTourSupplierCommissionDisplay",
    priceChanged: false,
  });

  push({
    layer: "currency",
    field: "supplier_net_after_supplier_commission_source_currency",
    rawValue: supplierNet != null ? formatTraceNumber(supplierNet) : NA,
    normalized: supplierNet,
    currency: srcCurrency !== NA ? srcCurrency : "—",
    notes: "נטו אחרי עמלת ספק (מקור: pricingSnapshot.supplier_net_price או ourTotalFare.netPrice)",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "currency",
    field: "supplier_net_ils_estimated",
    rawValue: supplierIls != null ? fmtIls(supplierIls) : NA,
    normalized: supplierIls,
    currency: "ILS",
    exchangeRate: fxRate != null ? formatTraceNumber(fxRate) : NA,
    notes: "ceil(net × שער) כששניהם קיימים — לא מחשב מחדש אם חסר שער",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "hr_margin",
    field: "hr_margin_percent",
    rawValue: hrPct != null ? formatPercent(hrPct) : NA,
    normalized: hrPct,
    currency: "—",
    commissionPercent: hrPct != null ? formatPercent(hrPct) : NA,
    notes: "מ־hr_margin_resolver_trace.final_hr_margin_percent או ourTotalFare",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "hr_margin",
    field: "hr_margin_amount_ils",
    rawValue: hrAmountIls != null ? fmtIls(hrAmountIls) : NA,
    normalized: hrAmountIls,
    currency: "ILS",
    commissionAmount: hrAmountIls != null ? fmtIls(hrAmountIls) : NA,
    notes: "מ־trace או hrCommission / snapshot",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "hr_margin",
    field: "hr_margin_source_and_feature",
    rawValue: [
      hrTrace?.source != null ? `source: ${hrTrace.source}` : null,
      hrTrace?.selected_feature_key != null ? `feature: ${hrTrace.selected_feature_key}` : null,
      hrTrace?.feature_key_attempted != null ? `attempted: ${hrTrace.feature_key_attempted}` : null,
      hrTrace?.commission_type != null ? `type: ${hrTrace.commission_type}` : null,
    ]
      .filter(Boolean)
      .join(" · ") || NA,
    normalized: null,
    currency: "—",
    commission_type: hrTrace?.commission_type != null ? String(hrTrace.commission_type) : NA,
    commission_plan: hrTrace?.source != null ? String(hrTrace.source) : NA,
    commission_value:
      hrTrace?.apply_hr_mode != null ? String(hrTrace.apply_hr_mode) : NA,
    notes: "מקור עמלת HR: hr_margin_resolver_trace (אינטייטלמנט / fallback וכו׳)",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "agent",
    field: "agent_price_before_hr_initial",
    rawValue: initialAgent != null ? fmtIls(initialAgent) : NA,
    normalized: initialAgent,
    currency: "ILS",
    notes: "ourTotalFare.initialPrice",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "agent",
    field: "agent_price_after_hr",
    rawValue: priceAfterHr != null ? fmtIls(priceAfterHr) : NA,
    normalized: priceAfterHr,
    currency: "ILS",
    notes: "ourTotalFare.priceWithCommission",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "fees",
    field: "credit_card_fee_ils",
    rawValue: ccFee != null ? fmtIls(ccFee) : NA,
    normalized: ccFee,
    currency: "ILS",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "fees",
    field: "handling_fee_ils",
    rawValue: handling != null ? fmtIls(handling) : NA,
    normalized: handling,
    currency: "ILS",
    notes: "תוספות / דמי טיפול אם קיימים ב־ourPrice",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "fees",
    field: "baggage_supplement_ils",
    rawValue: baggage != null ? fmtIls(baggage) : NA,
    normalized: baggage,
    currency: "ILS",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "display",
    field: "computed_final_with_cc_ils",
    rawValue: withCc != null ? fmtIls(withCc) : NA,
    normalized: withCc,
    currency: "ILS",
    afterRound: withCc,
    notes: "ourTotalFare.priceWithCommissionAndCreditCardFee או pricingSnapshot.final_price",
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "display",
    field: "ui_displayed_total_ils",
    rawValue: displayed != null ? fmtIls(displayed) : NA,
    normalized: displayed,
    currency: "ILS",
    display: displayed != null ? fmtIls(displayed) : NA,
    notes: "אותה לוגיקה כמו תצוגת כרטיס תוצאות (resolveOrganizedTourSearchCardPackageIls)",
    sourceFile: "features/admin-price-trace/utils/resolveOrganizedTourPrimaryPriceTrace.js",
    sourceFunction: "resolveOrganizedTourPrimaryPriceTrace",
    priceChanged: false,
  });

  const primaryByKey = (key) =>
    Array.isArray(primary.primaryRows) ? primary.primaryRows.find((r) => r.key === key) : null;

  push({
    layer: "agent",
    field: "price_before_hr_ils",
    rawValue: primaryByKey("before_hr")?.value ?? NA,
    normalized: primary.beforeHrIls,
    currency: "ILS",
    notes: [
      primary.beforeHrSource
        ? `לפני עמלת HR — ${primary.beforeHrSource}`
        : "לפני עמלת HR — ourTotalFare.initialPrice או supplier_net × FX",
      primary.beforeHrSrcSource || null,
    ]
      .filter(Boolean)
      .join(" · "),
    sourceFile: "features/admin-price-trace/utils/resolveOrganizedTourPrimaryPriceTrace.js",
    sourceFunction: "resolveOrganizedTourPrimaryPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "agent",
    field: "price_after_hr_ils",
    rawValue: primaryByKey("after_hr")?.value ?? NA,
    normalized: primary.afterHrIls,
    currency: "ILS",
    display: primaryByKey("after_hr")?.value ?? NA,
    notes: [
      "אחרי עמלת HR — ourTotalFare.priceWithCommission (לחבילה, לפני CC)",
      primary.afterHrSrcSource || null,
    ]
      .filter(Boolean)
      .join(" · "),
    sourceFile: "features/admin-price-trace/utils/resolveOrganizedTourPrimaryPriceTrace.js",
    sourceFunction: "resolveOrganizedTourPrimaryPriceTrace",
    priceChanged: false,
  });

  push({
    layer: "agent",
    field: "agent_net_final_ils",
    rawValue: primaryByKey("agent_net")?.value ?? primaryByKey("after_hr")?.value ?? NA,
    normalized: primary.agentNetIls ?? primary.afterHrIls,
    currency: "ILS",
    display: primaryByKey("agent_net")?.value ?? primaryByKey("after_hr")?.value ?? NA,
    notes: primaryByKey("agent_net")
      ? "נטו לסוכן סופי — מחיר הכרטיס שונה מאחרי HR"
      : "נטו לסוכן סופי = אחרי עמלת HR (אותו SSOT)",
    sourceFile: "features/admin-price-trace/utils/resolveOrganizedTourPrimaryPriceTrace.js",
    sourceFunction: "resolveOrganizedTourPrimaryPriceTrace",
    priceChanged: false,
  });

  const snapKeys =
    snap && typeof snap === "object"
      ? SNAPSHOT_KEYS_TRACE.split(",")
          .filter((k) => Object.prototype.hasOwnProperty.call(snap, k) && snap[k] != null)
          .join(", ") || NA
      : NA;

  push({
    layer: "meta",
    field: "pricing_snapshot_keys_present",
    rawValue: snapKeys,
    normalized: null,
    currency: "—",
    notes: `מפתחות צפויים (קיימים בפועל): ${snapKeys}`,
    sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
    sourceFunction: "buildOrganizedTourPriceTrace",
    priceChanged: false,
  });

  if (hrTrace && typeof hrTrace === "object") {
    let json;
    try {
      json = JSON.stringify(hrTrace, null, 0);
      if (json.length > 1200) json = `${json.slice(0, 1200)}…`;
    } catch {
      json = NA;
    }
    push({
      layer: "meta",
      field: "hr_margin_resolver_trace_json_truncated",
      rawValue: json,
      normalized: null,
      currency: "—",
      notes: "מקור מלא בשרת / pricingSnapshot",
      sourceFile: "features/admin-price-trace/utils/buildOrganizedTourPriceTrace.js",
      sourceFunction: "buildOrganizedTourPriceTrace",
      priceChanged: false,
    });
  }

  const customAnomalies = [];
  if (!snap) {
    customAnomalies.push({
      code: "org_tour_missing_pricing_snapshot",
      severity: "warning",
      message: "אין pricingSnapshot על ourPrice — חסרים פירוט מחיר ספק/HR מהמנוע.",
      relatedStepKeys: [],
    });
  }

  if (
    displayed != null &&
    priceAfterHr != null &&
    Math.abs(Number(displayed) - Number(priceAfterHr)) > 1.5
  ) {
    customAnomalies.push({
      code: "org_tour_display_vs_computed_mismatch",
      severity: "warning",
      message: `פער בין נטו לסוכן (${fmtIls(displayed)}) לבין priceWithCommission (${fmtIls(priceAfterHr)}).`,
      relatedStepKeys: [],
    });
  }

  if (
    displayed != null &&
    snapFinal != null &&
    Math.abs(Number(displayed) - Number(snapFinal)) > 1.5
  ) {
    customAnomalies.push({
      code: "org_tour_display_vs_snapshot_final",
      severity: "info",
      message: `המחיר המוצג שונה מ־pricingSnapshot.final_price (${fmtIls(snapFinal)} vs ${fmtIls(displayed)}).`,
      relatedStepKeys: [],
    });
  }

  const auto = detectPriceTraceAnomalies({ steps, summary: { finalDisplay: displayed } });
  const anomalies = [...customAnomalies, ...auto];

  const orgTourSupplierCommissionDisplay = [
    `${supplierCommission.supplier_commission_type} (${supplierCommission.supplier_commission_type_he})`,
    supplierCommission.supplier_commission_value_display,
    `ירידה: ${supplierCommission.supplier_commission_amount_source_currency_display}`,
    supplierCommission.supplier_commission_percent_effective_display &&
      `אפקטיבי מברוטו: ${supplierCommission.supplier_commission_percent_effective_display}`,
  ]
    .filter(Boolean)
    .join(" · ");

  if (import.meta.env?.DEV) {
    // #region agent log
    console.log("[ORG_TOUR_PRICE_TRACE_BUILD]", {
      context,
      entityId,
      stepCount: steps.length,
      hasSnapshot: Boolean(snap),
      hasHrTrace: Boolean(hrTrace),
      anomalyCount: anomalies.length,
    });
    console.log("[ORG_TOUR_SUPPLIER_COMMISSION_DISPLAY_TRACE]", {
      gross: supplierCommission.gross,
      deduction: supplierCommission.deduction,
      effectivePercent: supplierCommission.supplier_commission_percent_effective,
      commissionType: supplierCommission.supplier_commission_type,
      source: supplierCommission.type_source,
    });
    if (anomalies.length) {
      console.warn("[ORG_TOUR_PRICE_TRACE_WARNINGS]", {
        codes: anomalies.map((a) => a.code),
        messages: anomalies.map((a) => a.message),
      });
    }
    // #endregion
  }

  return {
    entityType: "organized_tour",
    entityId,
    entityName,
    context,
    summary: {
      apiRaw: supOrig.val,
      normalized: supplierNet,
      finalDisplay: displayed != null ? displayed : null,
      totalBeforeCommission: initialAgent,
      commissionTotal: hrAmountIls,
      exchangeRate: fxRate,
      displayCurrency: "ILS",
      sourceCurrency: srcCurrency !== NA ? srcCurrency : null,
      orgTourApiGrossDisplay:
        supOrig.val != null && srcCurrency !== NA
          ? `${formatTraceNumber(supOrig.val)} ${srcCurrency}`.trim()
          : supOrig.val != null
            ? `${formatTraceNumber(supOrig.val)}`
            : null,
      orgTourSupplierCommissionDisplay,
      orgTourSupplierNetDisplay:
        supplierNet != null && srcCurrency !== NA
          ? `${formatTraceNumber(supplierNet)} ${srcCurrency}`.trim()
          : supplierNet != null
            ? `${formatTraceNumber(supplierNet)}`
            : null,
      orgTourFxDisplay:
        fxRate != null && fxSource
          ? `${formatTraceNumber(fxRate)} — ${fxSource}`
          : fxRate != null
            ? `${formatTraceNumber(fxRate)}`
            : null,
      orgTourHrDisplay: hrAmountIls != null ? `${formatTraceNumber(hrAmountIls)} ₪` : null,
      orgTourFinalDisplay:
        withCc != null
          ? `${formatTraceNumber(withCc)} ₪`
          : displayed != null
            ? `${formatTraceNumber(displayed)} ₪`
            : null,
      orgTourApiUnit: primary.apiUnit,
      orgTourSupplierCommissionPerPersonDisplay:
        primary.primaryRows.find((r) => r.key === "supplier_commission_per_person")?.value ?? null,
      orgTourSupplierCommissionTotalDisplay:
        primary.primaryRows.find((r) => r.key === "supplier_commission_total")?.value ?? null,
      orgTourAdminDiscountDisplay:
        primary.primaryRows.find((r) => r.key === "admin_discount")?.value ?? null,
      orgTourAdminDiscountHint: primary.ruleMatchHint || null,
      orgTourBeforeHrDisplay: primary.primaryRows.find((r) => r.key === "before_hr")?.value ?? null,
      orgTourAfterHrDisplay: primary.primaryRows.find((r) => r.key === "after_hr")?.value ?? null,
      orgTourAgentNetDisplay:
        primary.primaryRows.find((r) => r.key === "agent_net")?.value ??
        primary.primaryRows.find((r) => r.key === "after_hr")?.value ??
        null,
      primaryRows: primary.primaryRows,
      extraRows: primary.extraRows,
      displayOrderNumberForAgent,
    },
    steps,
    anomalies,
    builtAt: new Date().toISOString(),
  };
}
